/**
 * PreDepartureCard.js
 *
 * FT-161 Phase 1: Pre-departure summary card for voyage log view
 * Displays route, crew, fuel, weather, and completed checks in a single card
 */

import React, { useState } from 'react';
import {
  ClipboardCheck,
  ChevronDown,
  ChevronUp,
  Check,
  Wind,
  Waves,
  Eye,
  Users,
  Fuel,
  UserPlus
} from 'lucide-react';
import { portLabel } from '../../../../../utils/voyage/portLabel';

const PreDepartureCard = ({
  voyage,
  checks = [],
  timestamp
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // Format time
  const formatTime = (ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  // Get route string
  const fromName = portLabel(voyage?.from, 'Unknown');
  const toName = portLabel(voyage?.to, 'Unknown');
  const route = `${fromName} → ${toName}`;

  // Get weather data
  const weather = voyage?.departureWeather || {};
  const windDisplay = weather.windDirection && (weather.windStrength || weather.windSpeed)
    ? `${weather.windDirection} ${weather.windStrength || weather.windSpeed}`
    : null;
  const seaState = weather.seaState;
  const visibility = weather.visibility;

  // Get crew/passenger counts
  const crewCount = voyage?.crewCount || 0;
  const passengerCount = voyage?.passengerCount || 0;

  // Get fuel
  const fuelLevel = voyage?.departureFuel;

  // Filter to only pre-departure checks and count
  const preDepartureChecks = checks.filter(c => c.checkType === 'pre_departure');
  const completedCount = preDepartureChecks.filter(c => c.completed).length;
  const totalCount = preDepartureChecks.length;

  // Get completed by (from last completed check)
  const lastCompletedCheck = preDepartureChecks.filter(c => c.completed).pop();
  const completedBy = lastCompletedCheck?.completedBy || voyage?.masterName || 'Unknown';

  return (
    <div className="bg-white rounded-lg shadow-sm border-l-4 border-cyan-600 p-4 mb-3">
      {/* Header Row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="h-5 w-5 text-cyan-600" />
          <span className="text-base font-semibold text-gray-900">Pre-Departure</span>
        </div>
        <span className="text-sm text-gray-400">{formatTime(timestamp)}</span>
      </div>

      {/* Route Subtitle */}
      <p className="text-sm text-gray-500 mb-3">{route}</p>

      {/* Stats Row */}
      <div className="flex items-center gap-4 mb-3 text-sm">
        <div className="flex items-center gap-1.5">
          <Users className="h-4 w-4 text-gray-400" />
          <span className="font-medium text-gray-900">Crew: {crewCount} POB</span>
        </div>
        {fuelLevel !== undefined && fuelLevel !== null && (
          <div className="flex items-center gap-1.5">
            <Fuel className="h-4 w-4 text-gray-400" />
            <span className="font-medium text-gray-900">Fuel: {fuelLevel}%</span>
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <UserPlus className="h-4 w-4 text-gray-400" />
          <span className="font-medium text-gray-900">Pax: {passengerCount}</span>
        </div>
      </div>

      {/* Weather Line (if available) */}
      {(windDisplay || seaState || visibility) && (
        <div className="flex items-center gap-4 mb-3 text-sm text-gray-600">
          {windDisplay && (
            <div className="flex items-center gap-1.5">
              <Wind className="h-4 w-4 text-gray-400" />
              <span>{windDisplay}</span>
            </div>
          )}
          {seaState && (
            <div className="flex items-center gap-1.5">
              <Waves className="h-4 w-4 text-gray-400" />
              <span>{seaState}</span>
            </div>
          )}
          {visibility && (
            <div className="flex items-center gap-1.5">
              <Eye className="h-4 w-4 text-gray-400" />
              <span>{visibility}</span>
            </div>
          )}
        </div>
      )}

      {/* Checks Section Header - Collapsible */}
      {preDepartureChecks.length > 0 && (
        <>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center justify-between w-full py-2 text-left"
          >
            <span className="text-sm font-medium text-gray-700">
              Pre-Departure Checks ({completedCount}/{totalCount})
            </span>
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-400" />
            )}
          </button>

          {/* Checks List */}
          {isExpanded && (
            <div className="space-y-1.5 mt-1">
              {preDepartureChecks.map((check, index) => (
                <div key={check.id || index} className="flex items-center gap-2 text-sm">
                  {check.completed ? (
                    <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                  ) : (
                    <div className="h-4 w-4 rounded-full border-2 border-gray-300 flex-shrink-0" />
                  )}
                  <span className={check.completed ? 'text-gray-700' : 'text-gray-400'}>
                    {check.text || check.label || check.name}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Footer */}
      <div className="mt-3 pt-2 border-t border-gray-100">
        <span className="text-xs text-gray-400">Completed by: {completedBy}</span>
      </div>
    </div>
  );
};

export default PreDepartureCard;
